import { untrack } from 'svelte';
import { shallowUpdateInFlight } from '../../runes/shallowUpdateInFlight/shallowUpdateInFlight.svelte';
import { goto } from '$app/navigation';
import { navigating } from '$app/state';

/**
 * Updates the current history entry's URL and `page.state` without navigating:
 * no `load` function re-runs and the scroll position and focus are left alone.
 * The replacement for SvelteKit's deprecated `replaceState`.
 *
 * It is a shallow `goto`, which differs from `replaceState` in two ways callers
 * should know about:
 *
 * - Navigation hooks run and `navigating` is set while it settles. Anything
 *   that reacts to a navigation (a loading indicator, closing a menu) has to
 *   ignore these: `isShallowUpdate` in a hook callback, `shallowUpdateInFlight`
 *   next to `navigating`.
 * - Like `replaceState`, it leaves `page.url` as it was: the new URL is on
 *   `page.shallow.url`. Code that compares against "the current URL" has to
 *   look there first, or it will keep seeing the old one.
 * - It takes over SvelteKit's navigation token, so one fired while a real
 *   navigation is in flight would abort that navigation. The page is being left
 *   at that point, so the update is skipped instead.
 */
export const replaceShallowState = async (url: string | URL, state: App.PageState) => {
	// Untracked: this may be called from inside an effect. Reading `navigating`
	// and the in-flight count there would subscribe that effect to them, and the
	// count is written just below, so the effect would re-run itself forever.
	const started = untrack(() => {
		if (navigating.to && shallowUpdateInFlight.count === 0) {
			return false;
		}

		shallowUpdateInFlight.count += 1;

		return true;
	});

	if (!started) {
		return;
	}

	try {
		await goto(url, { shallow: true, replace: true, state });
	} finally {
		untrack(() => {
			shallowUpdateInFlight.count -= 1;
		});
	}
};
